import { Accordion, BBSpinner, Box, Button, Flex, IconButton, Select, Text } from '@rocket/ui';
import { useEffect, useState } from 'react';
import { LuExternalLink } from 'react-icons/lu';
import { useFtueHost } from '../host';
import type { PlaybookModel } from '../onboarding-api';
import { getModels } from '../onboarding-api';
import { PlaybookSetupGuideEmbed } from './PlaybookSetupGuideEmbed';

interface Props {
  onSelectExisting: (playbookId: string, playbookName: string) => void;
  onCreateNew: () => void;
  isReturningUser?: boolean;
}

export function OnboardingWelcome({
  onSelectExisting,
  onCreateNew,
  isReturningUser = false,
}: Props) {
  const { bbAxios } = useFtueHost();
  const [models, setModels] = useState<Array<PlaybookModel>>([]);
  const [isLoadingModels, setIsLoadingModels] = useState(false);
  const [showSelect, setShowSelect] = useState(false);
  const [selectedId, setSelectedId] = useState('');

  useEffect(() => {
    setIsLoadingModels(true);
    getModels(bbAxios)
      .then(setModels)
      .catch(() => setModels([]))
      .finally(() => setIsLoadingModels(false));
  }, [bbAxios]);

  const handleConfirmExisting = () => {
    if (selectedId) {
      const selectedModel = models.find((m) => m._id === selectedId);
      onSelectExisting(selectedId, selectedModel?.name ?? '');
    }
  };

  return (
    <Flex.V gap="6" p="6">
      <Flex.V gap="2">
        <Text fontWeight="700" fontSize="xl">
          {isReturningUser ? 'Create a New Playbook' : 'Welcome to BlackBoiler'}
        </Text>
        <Text color="fg.muted" fontSize="sm">
          {isReturningUser ? (
            <>
              Build a custom playbook to define how BlackBoiler reviews your contracts. You can
              create a playbook from scratch by uploading sample redlined contracts, or select an
              existing playbook as a starting point and customize it with your edits.
            </>
          ) : (
            <>
              Let's set up your playbook so BlackBoiler can start reviewing contracts for you. You
              can select an existing playbook or create a new one by uploading sample contracts or a
              written playbook document.
            </>
          )}
        </Text>
      </Flex.V>

      {!showSelect ? (
        <Flex.V gap="3">
          <Button
            colorPalette="gray"
            onClick={() => setShowSelect(true)}
            disabled={isLoadingModels}
            w="100%"
          >
            {isLoadingModels ? <BBSpinner size="sm" /> : 'Select Existing Playbook'}
          </Button>
          <Button colorPalette="gray" variant="outline" onClick={onCreateNew} w="100%">
            Create New Playbook
          </Button>
        </Flex.V>
      ) : (
        <Flex.V gap="3">
          <Select
            label="Choose a playbook"
            options={models.map((m) => ({ value: m._id, label: m.name }))}
            placeholder="Select playbook…"
            onChange={(e) => setSelectedId(JSON.parse(e.target.value)[0] as string)}
          />
          <Flex.H gap="2">
            <Button variant="ghost" size="sm" onClick={() => setShowSelect(false)}>
              Back
            </Button>
            <Button
              colorPalette="gray"
              size="sm"
              disabled={!selectedId}
              onClick={handleConfirmExisting}
            >
              Confirm
            </Button>
          </Flex.H>
        </Flex.V>
      )}

      {/* Guide */}
      <Box width="100%" border="1px solid" borderColor="border" p="4" borderRadius="md">
        <Flex.H justify="space-between" align="center" mb="2">
          <Text fontSize="sm" fontWeight="semibold">
            How to create a playbook
          </Text>
          <IconButton
            size="xs"
            variant="ghost"
            aria-label="Open in new window"
            onClick={() => window.open('https://app.guideflow.com/embed/lpxx4zja1p', '_blank')}
          >
            <LuExternalLink />
          </IconButton>
        </Flex.H>
        <Accordion
          items={[
            {
              value: 'guide',
              title: 'Setup Guide',
              content: <PlaybookSetupGuideEmbed />,
            },
          ]}
          defaultValue={isReturningUser ? [] : ['guide']}
          multiple
          collapsible
        />
      </Box>
    </Flex.V>
  );
}
